import { useMergeRefs } from '@floating-ui/react'
import { forwardRef, memo, useCallback, useRef } from 'react'
import { PhoneInput } from 'react-international-phone'

import { cn } from '@repo/utils'

import type { ChangeEvent, InputHTMLAttributes, MutableRefObject } from 'react'
import type { PhoneInputProps } from 'react-international-phone'

export interface PhoneProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string
  phoneProps?: PhoneInputProps
  transparent?: boolean
}

export const InputPhone = memo(
  forwardRef<HTMLInputElement, PhoneProps>(({ className, onChange, phoneProps, transparent, ...props }, ref) => {
    const localRef = useRef<HTMLInputElement | null>(null)

    const { onChange: _droppedOnChange, ...inputProps } = { ...props, ...phoneProps?.inputProps }

    const childRef = useMergeRefs([ref, localRef])

    const handleChange = useCallback(() => {
      if (onChange) {
        onChange({
          bubbles: true,
          cancelable: true,
          currentTarget: localRef.current,
          defaultPrevented: false,
          nativeEvent: new Event('change', { bubbles: true, cancelable: true }),
          target: localRef.current,
          timeStamp: Date.now(),
          type: 'change',
        } as ChangeEvent<HTMLInputElement>)
      }
    }, [onChange])

    return (
      <PhoneInput
        defaultCountry="co"
        {...phoneProps}
        className={cn('InputPhone', className, transparent && 'bg-transparent')}
        inputProps={inputProps}
        inputRef={childRef as unknown as MutableRefObject<HTMLInputElement | null>}
        onChange={handleChange}
      />
    )
  }),
)

InputPhone.displayName = 'InputPhone'
